import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { kopsAPI } from "./kitchenApi";
import CountUp from "@/pages/operations/CountUp";
import {
  ChefHat, Clock, Star, AlertTriangle, Package, ClipboardList,
  Trash2, Timer, Repeat, Monitor, PlusCircle, Trash, Activity,
  ArrowRight, TrendingUp,
} from "lucide-react";

const Tile = ({ label, value, icon: Icon, accent = "text-hocp-gold", testid }) => (
  <div data-testid={testid}
    className="on-dark relative overflow-hidden bg-gradient-to-br from-[#0F1530] to-[#070B1F] rounded-2xl p-5 border border-white/10">
    <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-hocp-gold/5 blur-2xl pointer-events-none"/>
    <div className={`w-10 h-10 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center ${accent} relative`}>
      <Icon className="w-5 h-5" strokeWidth={1.75}/>
    </div>
    <div className="mt-4 relative">
      <div className="font-display text-[26px] font-semibold text-white tabular-nums leading-none">{value}</div>
      <div className="text-[11px] text-slate-200 font-medium mt-2 uppercase tracking-wider">{label}</div>
    </div>
  </div>
);

export default function KitchenDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => kopsAPI.dashboard().then(setD).catch(()=>{});
    load(); const iv = setInterval(load, 15000); return () => clearInterval(iv);
  }, []);
  const k = d?.kpis || {}; const a = d?.alerts || {};
  const top = d?.top_consumed || [];
  const feed = d?.activity || [];

  return (
    <div className="space-y-5" data-testid="kops-dashboard">
      {/* Hero */}
      <div className="on-dark relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0B1020] via-[#070B1F] to-[#0F1530] p-6 border border-white/10">
        <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-hocp-gold/10 blur-3xl pointer-events-none"/>
        <div className="text-[11px] uppercase tracking-widest text-slate-300 font-medium flex items-center gap-2">
          <ChefHat className="w-3.5 h-3.5"/> Kitchen Mission Control
          <span className="text-hocp-gold font-semibold tabular-nums">{d?.business_date || "—"}</span>
        </div>
        <div className="mt-3 font-display text-[42px] font-bold text-white tabular-nums leading-none">
          {(k.orders_waiting || 0) + (k.accepted || 0) + (k.preparing || 0)}
          <span className="text-slate-400 text-[22px]"> in flight</span>
        </div>
        <div className="text-[13px] text-slate-300 mt-2">
          {k.ready || 0} plated · {k.served_today || 0} served today · {k.avg_prep_time_min || 0}m avg prep
        </div>

        {/* Quick actions */}
        <div className="mt-5 flex flex-wrap gap-2" data-testid="kops-quick-actions">
          <Link to="/fnb/kds" data-testid="kops-qa-kds"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[12.5px] font-semibold hover:brightness-95 transition">
            <Monitor className="w-4 h-4"/> Open Kitchen Display
          </Link>
          <Link to="/kitchen/requests?new=1" data-testid="kops-qa-new-request"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-white/10 text-white text-[12.5px] font-medium hover:bg-white/15 border border-white/10 transition">
            <PlusCircle className="w-4 h-4"/> New Pantry Request
          </Link>
          <Link to="/kitchen/waste?new=1" data-testid="kops-qa-log-waste"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-white/10 text-white text-[12.5px] font-medium hover:bg-white/15 border border-white/10 transition">
            <Trash className="w-4 h-4"/> Log Waste
          </Link>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Tile label="Waiting"       value={<CountUp value={Number(k.orders_waiting)||0}/>} icon={Clock}         testid="kops-kpi-waiting"  accent="text-slate-300"/>
        <Tile label="Preparing"     value={<CountUp value={Number(k.preparing)||0}/>}      icon={ChefHat}       testid="kops-kpi-preparing" accent="text-amber-300"/>
        <Tile label="Ready"         value={<CountUp value={Number(k.ready)||0}/>}          icon={Star}          testid="kops-kpi-ready"    accent="text-emerald-300"/>
        <Tile label="Delayed"       value={<CountUp value={Number(k.delayed)||0}/>}        icon={AlertTriangle} testid="kops-kpi-delayed"  accent={Number(k.delayed)>0?"text-rose-300":"text-slate-300"}/>
        <Tile label="Longest Wait"  value={`${k.longest_wait_min||0}m`}                     icon={Timer}         testid="kops-kpi-longest"  accent="text-fuchsia-300"/>
        <Tile label="VIP Pending"   value={<CountUp value={Number(k.vip_pending)||0}/>}      icon={Star}          testid="kops-kpi-vip"      accent="text-hocp-gold"/>
        <Tile label="Pantry Requests Awaiting" value={<CountUp value={Number(k.open_pantry_requests)||0}/>} icon={ClipboardList} testid="kops-kpi-open-reqs" accent="text-sky-300"/>
        <Tile label="Low Stock Ingredients"    value={<CountUp value={Number(k.low_stock_items)||0}/>}       icon={Package}       testid="kops-kpi-low-stock" accent={Number(k.low_stock_items)>0?"text-rose-300":"text-emerald-300"}/>
        <Tile label="Stock Alerts"             value={<CountUp value={Number(k.stock_alerts_count)||0}/>}    icon={AlertTriangle} testid="kops-kpi-stock-alerts" accent={Number(k.stock_alerts_count)>0?"text-rose-300":"text-slate-300"}/>
        <Tile label="Waste Logged Today"       value={<CountUp value={Number(k.waste_events_today)||0}/>}    icon={Trash2}        testid="kops-kpi-waste"     accent="text-amber-300"/>
      </div>

      {/* Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <AlertCard title="Below Minimum" tone="rose" data={a.below_min || []} testid="kops-alert-below-min"
          renderRow={p => <><span className="flex-1 truncate">{p.item_name}</span><span className="font-mono tabular-nums">{p.current_stock}/{p.min_stock} {p.unit}</span></>}/>
        <AlertCard title="Running Out Today" tone="amber" data={a.running_out_today || []} testid="kops-alert-running-out"
          renderRow={p => <><span className="flex-1 truncate">{p.item_name}</span><span className="font-mono tabular-nums text-[11px]">{p.days_remaining}d · {p.avg_daily_usage}/d</span></>}/>
        <AlertCard title="Repeated Requests (last 3 days)" tone="violet" data={a.repeated_requests || []} testid="kops-alert-repeated"
          renderRow={p => <><span className="flex-1 truncate">{p.item_name}</span><span className="font-mono tabular-nums flex items-center gap-1"><Repeat className="w-3 h-3"/>{p.count}×</span></>}/>
      </div>

      {/* Top consumed + Activity feed */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Top 5 consumed today */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="kops-top-consumed">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-hocp-navy"/>
            <div className="font-display text-[14px] font-semibold text-hocp-navy">Top 5 Consumed Today</div>
          </div>
          {top.length === 0 ? (
            <div className="p-8 text-center text-[12.5px] text-slate-400">No consumption logged yet today.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {top.map((t, i) => {
                const max = Number(top[0]?.qty || 1);
                const pct = Math.max(6, Math.round((Number(t.qty) / max) * 100));
                return (
                  <div key={t.item_name} className="px-4 py-3" data-testid={`kops-top-${i}`}>
                    <div className="flex items-center justify-between text-[13px]">
                      <span className="font-medium text-hocp-navy truncate">{i+1}. {t.item_name}</span>
                      <span className="mono tabular-nums text-slate-500">{t.qty}</span>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-hocp-gold" style={{ width: `${pct}%` }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Activity Feed */}
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="kops-activity-feed">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <Activity className="w-4 h-4 text-hocp-navy"/>
            <div className="font-display text-[14px] font-semibold text-hocp-navy">Kitchen Activity Feed</div>
            <Link to="/kitchen/requests" className="ml-auto inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-hocp-navy">
              View all <ArrowRight className="w-3.5 h-3.5"/>
            </Link>
          </div>
          {feed.length === 0 ? (
            <div className="p-8 text-center text-[12.5px] text-slate-400">No kitchen events yet.</div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[380px] overflow-y-auto">
              {feed.map((e, i) => <FeedRow key={i} e={e} testid={`kops-activity-${i}`}/>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FeedRow({ e, testid }) {
  const iconOf = () => {
    if (e.kind?.startsWith("waste."))                    return { I: Trash2, cls: "text-amber-600 bg-amber-50" };
    if (e.kind?.includes("adjustment"))                  return { I: AlertTriangle, cls: "text-rose-600 bg-rose-50" };
    if (e.kind === "request.approved")                   return { I: ClipboardList, cls: "text-emerald-600 bg-emerald-50" };
    if (e.kind === "request.denied")                     return { I: ClipboardList, cls: "text-rose-600 bg-rose-50" };
    if (e.kind === "request.kitchen_received")           return { I: Package, cls: "text-cyan-600 bg-cyan-50" };
    if (e.kind === "request.delivered")                  return { I: Package, cls: "text-teal-600 bg-teal-50" };
    if (e.kind?.startsWith("request."))                  return { I: ClipboardList, cls: "text-sky-600 bg-sky-50" };
    if (e.kind?.includes("transfer"))                    return { I: Package, cls: "text-violet-600 bg-violet-50" };
    return { I: Activity, cls: "text-slate-600 bg-slate-50" };
  };
  const { I, cls } = iconOf();
  const label = (e.kind || "").replace("request.", "Request · ").replace("waste.", "Waste · ")
                              .replace("supply.pantry_transfer", "Inventory Transfer")
                              .replace("supply.waste_adjustment", "Inventory Adjustment")
                              .replace(/_/g, " ");
  const when = e.at ? new Date(e.at) : null;
  const timeStr = when ? when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div className="px-4 py-2.5 flex items-start gap-3" data-testid={testid}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${cls}`}>
        <I className="w-4 h-4" strokeWidth={2}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[13px]">
          <span className="font-medium text-hocp-navy capitalize truncate">{label}</span>
          {e.code && <span className="mono text-[11px] text-slate-500">· {e.code}</span>}
        </div>
        <div className="text-[12px] text-slate-500 truncate">{e.detail}</div>
      </div>
      <div className="text-[11px] mono text-slate-400 tabular-nums shrink-0">
        {timeStr}
        {e.actor && <div className="text-right text-slate-400 truncate max-w-[110px]">{e.actor}</div>}
      </div>
    </div>
  );
}

function AlertCard({ title, tone, data, renderRow, testid }) {
  const toneMap = { rose:"bg-rose-50 text-rose-800 border-rose-200",
                    amber:"bg-amber-50 text-amber-800 border-amber-200",
                    violet:"bg-violet-50 text-violet-800 border-violet-200" };
  return (
    <div className={`border rounded-2xl overflow-hidden ${toneMap[tone]}`} data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-current/10 font-display text-[13px] font-semibold flex items-center justify-between">
        {title}<span className="tabular-nums font-mono text-[12px]">{data.length}</span>
      </div>
      <div className="bg-white">
        {data.length === 0 ? (
          <div className="p-6 text-center text-[12px] text-slate-400">All clear.</div>
        ) : data.slice(0, 8).map((p, i) => (
          <div key={i} className="flex items-center gap-2 px-4 py-2 text-[13px] border-b border-slate-100 last:border-0">
            {renderRow(p)}
          </div>
        ))}
      </div>
    </div>
  );
}
